import { useEffect, useState } from 'react';

interface BrowserUIInfo {
    hasPersistentBrowserUI: boolean;
    bottomOffset: number;
    requiresManualPadding: boolean;
    orientation: 'portrait' | 'landscape';
}

interface DeviceInfo {
    platform: any;
    type: any;
    browser: any;
}

export const useBrowserUIDetection = (deviceInfo: DeviceInfo): BrowserUIInfo => {
    const [browserUIInfo, setBrowserUIInfo] = useState<BrowserUIInfo>({
        hasPersistentBrowserUI: false,
        bottomOffset: 0,
        requiresManualPadding: false,
        orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
    });

    useEffect(() => {
        const detectBrowserUI = () => {
            const orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
            let hasPersistentUI = false;
            let bottomOffset = 0;
            let requiresPadding = false;

            // Method 1: Compare window vs document height
            const browserChromeHeight = window.innerHeight - document.documentElement.clientHeight;

            // Method 2: Visual Viewport API (most accurate)
            const visualViewportOffset = window.visualViewport
                ? window.innerHeight - window.visualViewport.height
                : 0;

            // Take the maximum of both methods
            const uiCoverage = Math.max(browserChromeHeight, visualViewportOffset);

            // Android Tablet Cases
            if (deviceInfo.platform === 'android' && deviceInfo.type === 'tablet') {
                // Chrome/Firefox on Android tablets
                if (['chrome', 'firefox'].includes(deviceInfo.browser)) {
                    hasPersistentUI = uiCoverage > 50 || orientation === 'landscape';
                    bottomOffset = Math.max(uiCoverage, window.innerHeight * 10/100);
                    requiresPadding = true;
                }
            }

            // iOS Tablet Cases
            else if (deviceInfo.platform === 'ios' && deviceInfo.type === 'tablet') {
                // Safari landscape mode on iPad
                if (deviceInfo.browser === 'safari' && orientation === 'landscape') {
                    bottomOffset = window.innerHeight * 2.5/100; // Add small buffer
                }
            }

            // WebView browsers (Facebook, Twitter in-app)
            if (/FBAN|Twitter/i.test(navigator.userAgent)) {
                hasPersistentUI = true;
                bottomOffset = Math.max(bottomOffset, window.innerHeight * 5/100);
                requiresPadding = true;
            }

            setBrowserUIInfo({
                hasPersistentBrowserUI: hasPersistentUI,
                bottomOffset,
                requiresManualPadding: requiresPadding || hasPersistentUI,
                orientation
            });
        };

        // Initial detection
        detectBrowserUI();

        // Modern orientation detection
        const handleOrientationChange = () => {
            detectBrowserUI();
        };

        if (window.screen.orientation) {
            window.screen.orientation.addEventListener('change', handleOrientationChange);
        }

        return () => {

            if (window.screen.orientation) {
                window.screen.orientation.removeEventListener('change', handleOrientationChange);
            }
        };
    }, [deviceInfo]);

    return browserUIInfo;
};